import { useContext, useState } from 'react';
import {
    Button,
    Card,
    CardBody,
    CardText,
    CardTitle,
    Col,
    Row,
    Badge,
} from 'reactstrap';
import { RecentDonationsCard } from '~/containers/Dashboard/components/Donations';
import {
    FundraisingStatusCard,
    UpcomingFundaisersCard,
} from '~/containers/Dashboard/components/Fundraisers';
import { MaterialsCard } from '~/containers/Dashboard/components/Materials';
import { ContactCard } from '~/shared/components/cards/ContactCard';
import {
    CampaignSerializer,
    Contact,
    IApplicationStatus,
    ICampaignStatsItem,
    IFundraiserItem,
    IMaterialItem,
} from '~/types/campaign';
import { getDashColor } from '../icons';
import { Link } from 'react-router-dom';
import { AuthContext } from '~/context/auth';
import { CampaignContext } from '~/context/campaign';
import { syncMyCampaign } from '~/services/campaign';
import toast from 'react-hot-toast';

export interface CampaignItemsSectionProps {
    campaign: CampaignSerializer;
    fundraisers: IFundraiserItem[];
    donations: ICampaignStatsItem[];
}

export const CampaignItemsSection = ({
    campaign,
    fundraisers,
    donations,
}: CampaignItemsSectionProps) => {
    const unpublished = campaign?.campaign_status === 'Unpublished';
    if (unpublished) {
        return <></>;
    }
    return (
        <Row className='row-cols-1 row-cols-md-2 g-4 my-4'>
            {/* <Col>
        <FundraisingStatusCard app_item={campaign} />
      </Col> */}
            <Col>
                <RecentDonationsCard app_items={donations} />
            </Col>
            <Col>
                <UpcomingFundaisersCard app_items={fundraisers} />
            </Col>
            {/* <Col>
        <MaterialsCard app_items={materials} />
      </Col> */}
        </Row>
    );
};

export const ManageCampaignCard = () => {
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [publishing, setPublishing] = useState(false);
    if (!campaign) {
        return <></>;
    }
    const { campaignDetail } = campaign;
    const hideStatuses = ['Disbursable'];

    const syncCampaign = async () => {
        try {
            setPublishing(true);
            const loading = toast.loading('Publishing campaign...');
            const resp = await syncMyCampaign();
            setPublishing(false);
            toast.success('Campaign published!', {
                id: loading,
                duration: 6000,
            });
            const wordpress_id = resp.ID;
            const wordpress_permalink = resp.permalink;
            campaignDispatch({
                type: 'set_wordpress',
                payload: { wordpress_id, wordpress_permalink },
            });
        } catch (e) {
            console.log('e: ', e);
            setPublishing(false);
            toast.dismiss();
            toast.error('There was an error publishing your campaign');
        }
    };

    if (hideStatuses.includes(campaignDetail?.campaign_status)) {
        return <></>;
    }

    return (
        <Card
            className='mb-5'
            style={{ background: getDashColor('supporters') }}
        >
            <CardBody>
                <CardTitle
                    style={{
                        fontSize: '26px',
                        fontWeight: '900',
                    }}
                >
                    Manage Campaign
                </CardTitle>
                <CardText>
                    Personalize your goal and story, add a campaign picture,
                    post Updates and Albums, read and manage Guestbook posts,
                    and create and promote Fundraisers.
                </CardText>
                <Link to='/campaign/edit' className='btn btn-primary'>
                    Edit Campaign
                </Link>
                {campaignDetail?.wordpress_permalink ? (
                    // <Button
                    //   color='outline-dark'
                    //   onClick={() =>
                    //     window.open(campaignDetail?.wordpress_permalink, '_blank')
                    //   }
                    // >
                    //   View Campaign
                    // </Button>
                    <Button
                        color='outline-dark'
                        onClick={() =>
                            window.open(
                                `https://dev.helphopelive.org/campaign/${campaignDetail?.campaign_id}`,
                                '_blank'
                            )
                        }
                    >
                        View Campaign
                    </Button>
                ) : (
                    <Button
                        color='outline-dark'
                        onClick={syncCampaign}
                        disabled={publishing}
                    >
                        Publish Campaign
                    </Button>
                )}
            </CardBody>
        </Card>
    );
};
export interface CampaignHeaderSectionProps {
    contact?: Contact;
    leftcard: any;
    banner?: any;
}

export const CampaignHeaderSection = ({
    leftcard,
    contact,
    banner,
}: CampaignHeaderSectionProps) => {
    return (
        <div className='mb-md-5'>
            <Row style={{ alignItems: 'flex-start' }}>
                <Col lg={banner ? 4 : 7} className='mb-4 mb-lg-0'>
                    {leftcard}
                </Col>
                {banner && (
                    <Col lg={4} className='mb-4 mb-lg-0'>
                        {banner}
                    </Col>
                )}
                <Col
                    lg={banner ? 4 : 5}
                    className='d-lg-flex justify-content-end'
                >
                    <ContactCard contact={contact} />
                </Col>
            </Row>
        </div>
    );
};

export interface CampaignWelcomeCardProps {
    detail: CampaignSerializer;
    status: IApplicationStatus;
}

export const CampaignWelcomeCard = ({
    detail,
    status,
}: CampaignWelcomeCardProps) => {
    const [{ person }, userDispatch] = useContext(AuthContext);
    const privacy_flag = detail?.privacy_flag ? true : false;

    const campaignStatuses = [
        { label: 'Funded', color: '#01aeb4', new_label: 'Live' },
        { label: 'Disbursable', color: '#f47d29' },
        { label: 'Approved', color: 'rgba(1, 174, 180, 0.25)' },
        { label: 'Unpublished', color: 'black', new_label: 'In Progress' },
        { label: 'Closed', color: '#952e46' },
    ];
    if (
        campaignStatuses.map((x) => x.label).includes(detail?.campaign_status)
    ) {
        const campaignStatus = campaignStatuses.find(
            (x) => x.label === detail.campaign_status
        );
        return (
            <Row>
                <Col>
                    <h1 className='h3 mb-1'>Welcome, {person?.first_name}</h1>
                    <Badge
                        color='none'
                        style={{
                            backgroundColor: campaignStatus?.color,
                        }}
                    >
                        Campaign{' '}
                        {campaignStatus?.new_label
                            ? campaignStatus?.new_label
                            : detail?.campaign_status}
                    </Badge>
                    {detail?.direct_vendor_pay_only && (
                        <Badge
                            color='none'
                            style={{
                                backgroundColor: '#FEBF3E',
                                color: '#131212',
                            }}
                            className='ms-2'
                        >
                            Direct Vendor Pay Only
                        </Badge>
                    )}
                    {privacy_flag && (
                        <Badge
                            color='none'
                            style={{ backgroundColor: '#f47d29' }}
                            className='ms-2'
                        >
                            Private
                        </Badge>
                    )}
                </Col>
            </Row>
        );
    }
    // Some campaigns don't have an application so status
    // will be undefined or empty
    return (
        <Row>
            <Col>
                <h1 className='h3 mb-1'>Welcome, {person?.first_name}</h1>
                <Badge
                    color='none'
                    style={{
                        backgroundColor: getDashColor(
                            status?.status?.toLowerCase()
                        ),
                    }}
                >
                    Application: {status?.status}
                </Badge>
                {detail?.direct_vendor_pay_only && (
                    <Badge
                        color='none'
                        style={{
                            backgroundColor: '#FEBF3E',
                            color: '#131212',
                        }}
                        className='ms-2'
                    >
                        Direct Vendor Pay Only
                    </Badge>
                )}
            </Col>
        </Row>
    );
};
